import { useState, useEffect } from 'react';
import { FaCode, FaRocket, FaStar, FaCog } from 'react-icons/fa';

const LoadingScreen = () => {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Initializing...');

  useEffect(() => {
    const loadingSteps = [
      { text: 'Initializing...', progress: 20 },
      { text: 'Loading Portfolio...', progress: 40 },
      { text: 'Setting up animations...', progress: 60 },
      { text: 'Preparing experience...', progress: 80 },
      { text: 'Almost ready...', progress: 95 },
      { text: 'Welcome!', progress: 100 }
    ];

    let stepIndex = 0;
    
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        const currentStep = loadingSteps[stepIndex];
        
        if (prevProgress >= currentStep.progress && stepIndex < loadingSteps.length - 1) {
          stepIndex++;
          setLoadingText(loadingSteps[stepIndex].text);
        }
        
        if (prevProgress >= 100) {
          clearInterval(timer);
          setTimeout(() => setLoading(false), 100); // Reduced to 100ms for instant transition
          return 100;
        }
        return prevProgress + 4; // Increased from 2.5 to speed up more
      });
    }, 25); // Reduced from 40ms to 25ms

    return () => clearInterval(timer);
  }, []);

  if (!loading) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a1a] via-[#1a1a2e] to-[#16213e] flex items-center justify-center z-[9999] overflow-hidden">
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-pink-400 rounded-full animate-float opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${4 + Math.random() * 3}s`
            }}
          />
        ))}
        
        {/* Larger floating elements */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`large-${i}`}
            className="absolute w-2 h-2 bg-yellow-400 rounded-full animate-float opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${5 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="text-center relative z-10">
        {/* Animated logo section */}
        <div className="mb-8 relative">
          <div className="w-40 h-40 mx-auto mb-6 relative">
            {/* Outer rotating ring */}
            <div className="absolute inset-0 border-4 border-transparent border-t-pink-500 border-r-purple-500 rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>
            
            {/* Middle rotating ring */}
            <div className="absolute inset-3 border-4 border-transparent border-b-yellow-400 border-l-blue-400 rounded-full animate-spin" style={{ animationDuration: '3s', animationDirection: 'reverse' }}></div>
            
            {/* Inner pulsing circle */}
            <div className="absolute inset-6 bg-gradient-to-br from-pink-500 via-purple-500 to-yellow-400 rounded-full animate-pulse opacity-20"></div>
            
            {/* Center icon container */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                {/* Main code icon */}
                <FaCode className="w-16 h-16 text-white animate-pulse" />
                
                {/* Orbiting mini icons */}
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '4s' }}>
                  <FaStar className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-4 h-4 text-yellow-400" />
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '5s', animationDirection: 'reverse' }}>
                  <FaCog className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-4 h-4 text-purple-400 animate-spin" style={{ animationDuration: '2s' }} />
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '6s' }}>
                  <FaRocket className="absolute -left-6 top-1/2 transform -translate-y-1/2 w-4 h-4 text-pink-400" />
                </div>
              </div>
            </div>
            
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-yellow-400/20 rounded-full blur-xl animate-pulse"></div>
          </div>
        </div>

        {/* Name and title */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-3 animate-fade-in-up">
            Sabarisan P
          </h1>
          <p className="text-pink-400 text-lg animate-fade-in" style={{ animationDelay: '0.3s' }}>
            Full Stack Developer
          </p>
          <div className="flex justify-center space-x-4 mt-4 text-sm text-gray-400">
            <span className="animate-fade-in" style={{ animationDelay: '0.5s' }}>PHP</span>
            <span className="animate-fade-in" style={{ animationDelay: '0.7s' }}>React</span>
            <span className="animate-fade-in" style={{ animationDelay: '0.9s' }}>Node.js</span>
          </div>
        </div>

        {/* Enhanced progress section with box */}
        <div className="w-80 mx-auto mb-6">
          {/* Progress Box Container */}
          <div className="bg-gray-800 rounded-2xl p-5 border-2 border-pink-500 shadow-xl">
            <div className="flex justify-between text-sm text-gray-200 mb-3">
              <span className="animate-pulse font-medium">{loadingText}</span>
              <span className="font-mono font-bold text-pink-400">{Math.round(progress)}%</span>
            </div>
            
            {/* Progress bar with clear visibility */}
            <div className="relative w-full bg-black rounded-full h-4 overflow-hidden border-2 border-transparent bg-clip-padding"
                 style={{ 
                   borderImage: 'linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899) 1'
                 }}>
              <div 
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              >
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced animated dots */}
        <div className="flex justify-center space-x-3">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-3 h-3 rounded-full animate-loading-dots"
              style={{ 
                animationDelay: `${i * 0.15}s`,
                backgroundColor: ['#ec4899', '#8b5cf6', '#f59e0b', '#06b6d4', '#10b981'][i]
              }}
            />
          ))}
        </div>

        {/* Loading status indicator */}
        <div className="mt-8 text-xs text-gray-500 animate-pulse">
          Crafting an amazing experience...
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;